import { useState } from "react";
import { MapPin, Phone, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { motion } from "motion/react";
import apiClient from "../services/api.service";
import { CONTACT } from "../config/contact";

const CONTACT_CARDS = [
  {
    icon: MapPin,
    title: "Location",
    lines: [CONTACT.address.line1, CONTACT.address.line2],
  },
  {
    icon: Phone,
    title: "Phone",
    lines: CONTACT.phones,
  },
  {
    icon: Mail,
    title: "Email",
    lines: [CONTACT.emails.general, CONTACT.emails.bookings],
  },
];

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

export function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("loading");
    setErrorMessage("");

    try {
      await apiClient.post("/contact", formData);
      setFormStatus("success");
      setFormData({ firstName: "", lastName: "", email: "", phone: "", message: "" });
      setTimeout(() => setFormStatus("idle"), 5000);
    } catch (error: any) {
      setFormStatus("error");
      setErrorMessage(error.response?.data?.message || "Failed to send message. Please try again.");
      setTimeout(() => setFormStatus("idle"), 5000);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-16"
        >
          <h2 className="text-[#0f1f3d] mb-4 text-4xl lg:text-5xl">
            Get in touch
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Ready to make Majesty Hostel your home? We're here to answer all your questions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
              <h3 className="text-[#0f1f3d] text-2xl mb-6">Send us a message</h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-firstName" className="block text-[#0f1f3d] mb-2">First Name</label>
                    <Input
                      id="contact-firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Ama"
                      className="border border-gray-200 focus:border-[#d4af37] rounded-xl h-12 transition-colors"
                      required
                      disabled={formStatus === "loading"}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-lastName" className="block text-[#0f1f3d] mb-2">Last Name</label>
                    <Input
                      id="contact-lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Mensah"
                      className="border border-gray-200 focus:border-[#d4af37] rounded-xl h-12 transition-colors"
                      required
                      disabled={formStatus === "loading"}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-[#0f1f3d] mb-2">Email</label>
                  <Input
                    id="contact-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    placeholder="you@example.com"
                    className="border border-gray-200 focus:border-[#d4af37] rounded-xl h-12 transition-colors"
                    required
                    disabled={formStatus === "loading"}
                  />
                </div>

                <div>
                  <label htmlFor="contact-phone" className="block text-[#0f1f3d] mb-2">Phone Number</label>
                  <Input
                    id="contact-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    type="tel"
                    placeholder="+233 XX XXX XXXX"
                    className="border border-gray-200 focus:border-[#d4af37] rounded-xl h-12 transition-colors"
                    required
                    disabled={formStatus === "loading"}
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-[#0f1f3d] mb-2">Message</label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your accommodation needs..."
                    rows={5}
                    className="border border-gray-200 focus:border-[#d4af37] rounded-xl resize-none transition-colors"
                    required
                    disabled={formStatus === "loading"}
                  />
                </div>

                {formStatus === "error" && (
                  <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                    {errorMessage}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="gold"
                  className="w-full h-12 rounded-xl shadow-lg"
                  disabled={formStatus === "loading" || formStatus === "success"}
                >
                  {formStatus === "loading" ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : formStatus === "success" ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Contact Cards */}
            <div className="space-y-4">
              {CONTACT_CARDS.map((card) => (
                <div
                  key={card.title}
                  className="bg-white rounded-2xl p-6 border border-gray-200 transition-all duration-300 hover:border-[#d4af37]/30 hover:shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d4af37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <card.icon className="w-6 h-6 text-[#d4af37]" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-[#0f1f3d] font-semibold mb-1">{card.title}</h4>
                      <p className="text-gray-600">
                        {card.lines.map((line, i) => (
                          <span key={line}>
                            {i > 0 && <br />}
                            {line}
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 h-[300px]">
              <iframe
                src={CONTACT.map.embedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Majesty Hostel Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
